import { Component, effect, OnDestroy, OnInit, ViewChild, AfterViewInit, inject, Injector } from '@angular/core';
import { Table } from 'primeng/table';
import { Popover } from 'primeng/popover';
import { Subscription } from 'rxjs/internal/Subscription';
import { ColumnName, ItemState } from 'src/app/shared/models/core';
import { IUnloadedTasksMap } from 'src/app/shared/models/task-unloaded';
import { DataAccessService } from 'src/app/shared/services/data-access/data-access.service';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { TaskMovementService } from 'src/app/shared/services/operations/task-movement.service';
import { TaskInfoService } from 'src/app/shared/services/task-info/task-info.service';
import { TaskbankColumnService } from '../column-service/taskbank-column.service';
import { Navigation } from 'src/app/shared/entity/navigation';
import { TBTabName } from 'src/app/shared/entity/enums';
import { SortingService } from 'src/app/shared/services/operations/sorting.service';
import { SelectedPreferencesENUM } from 'src/app/shared/models/preference';
import { KeyValue } from '@angular/common';
import { SelectedTBRowKPI } from 'src/app/shared/entity/utility-interfaces';
import { INDICATOR_DEFS, IndicatorDef, getEventsByTab, getServiceCodesByTab } from 'src/app/shared/entity/indicator.const';
import { TaskBankFilterStateService } from 'src/app/shared/services/task-bank-filter/task-bank-filter-state.service';
import { FilterService } from 'primeng/api';

@Component({
    selector: 'app-unloaded',
    templateUrl: './unloaded.component.html',
    styleUrls: ['../../../../../shared/css/primeng-table.component.scss'],
    standalone: false
})
export class UnloadedComponent implements OnDestroy, OnInit, AfterViewInit {
  private readonly injector = inject(Injector);
  @ViewChild('dataTable') table: Table;
  @ViewChild('indicatorPanel') indicatorPanel: Popover;
  visibleColumns: any[] =[]
  showTable: boolean = true;
  private currentPreferenceId: string = '';
  private readonly taskFilterField = 'task_filter_keys';
  private readonly taskFilterKeys = Array.from(
    new Set([
      ...getServiceCodesByTab(TBTabName.Unloaded).map((item) => item.key),
      ...getEventsByTab(TBTabName.Unloaded).map((item) => item.key),
    ])
  );
  unloadedData: IUnloadedTasksMap[] = [];
  taskBankSubscription:Subscription[] = [];
  itemState=ItemState;
  navigation = Navigation;
  activeIndicators: IndicatorDef[] = [];
  selectedTaskFilterServiceCode: string | null = null;
  selectedTaskFilterEvent: string | null = null;

  /**
   * Displays the indicator panel for a specific row, showing the active indicators
   * @param event The event that triggered the panel display
   * @param row The row data containing the indicators
   */
  showIndicatorPanel(event: Event, row: any): void {
    this.activeIndicators = row.indicators ?? [];
    this.indicatorPanel.toggle(event);
  }

  //getter and setter 
  get isCustomSort(): boolean{
    return this.sortingService.isCustomSort?? false;
  }
  // getter for select all value
  get isSelectAll() {
    return this.taskMovementService.isSelectedAll;
  }

  get showSelectedTaskKPI(): KeyValue<keyof SelectedTBRowKPI, string| number>[]{
    return this.taskMovementService.selectedTaskKPI;
  }

  constructor(public taskInfoService: TaskInfoService
    , public dataAccessService: DataAccessService
    , public dataRepositoryService: DataRepositoryService
    , private readonly taskBankColumnService: TaskbankColumnService
    , public taskMovementService: TaskMovementService
    , public readonly sortingService: SortingService
    , private readonly filterService: FilterService
    , private readonly taskBankFilterStateService: TaskBankFilterStateService
  ) {
    this.filterService.register('task-filter-any', (rowValue: string[] | undefined, filterValue: string[] | null) => {
      if (!filterValue?.length) return true;
      if (!Array.isArray(rowValue) || !rowValue.length) return false;
      return filterValue.some((key) => rowValue.includes(key));
    });

    this.sortedTaskSelectionEffect();
    this.selectedColumnEffect();
    this.taskBankFilterSignalEffect();
  }

  ngOnInit(): void {
    this.unloadedSelectedSubs();
    this.polygonTaskSelectSubs$();
    this.polygonTaskDeselectSubs$();
  }

  ngAfterViewInit(): void {
    this.applyTaskBankSelectionFilter();
  }

  /**
   * Consumes task filter state using Angular signals and applies it only for Unloaded tab.
   * Automatically cleaned up on component destruction.
   */
  taskBankFilterSignalEffect(): void {
    effect(() => {
      const taskFilterState = this.taskBankFilterStateService.taskBankFilterState();
      const isUnloadedTab = this.taskInfoService.selectedTabName?.toLowerCase() === TBTabName.Unloaded.toLowerCase();

      if (!isUnloadedTab) {
        this.selectedTaskFilterServiceCode = null;
        this.selectedTaskFilterEvent = null;
        this.applyTaskBankSelectionFilter();
        return;
      }

      this.selectedTaskFilterServiceCode = taskFilterState.serviceCodeKey;
      this.selectedTaskFilterEvent = taskFilterState.eventKey;
      this.applyTaskBankSelectionFilter();
    }, { injector: this.injector });
  }

  /**
   * Method handles subscription for unloaded item collection - full list and delta version
   */
  unloadedSelectedSubs(){
    this.taskBankSubscription.push(
      this.dataAccessService.isUnloadedSelected$.subscribe((unloadedData) => {
        if (unloadedData) {
          const unloadedCollection = this.getUnloadedCollection();

          //check if custom sort is applied on the table
          if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.unloadedTbCollection.size) {
            if (unloadedData?.isDeltaUpdate) {
              this.setUnloadedSourceData(this.sortingService.processCustomSortingWithDelta());
            }
            this.sortingService.isCustomSort = true;
          } else {
            this.setUnloadedSourceData(unloadedCollection);
          }
          this.updateAllowedTaskMoveData(unloadedCollection);
        }
      })
    );
  }

  /**
   * Method handles subscription for updateColumn event. Automatically cleaned up on destroy.
   */
  selectedColumnEffect() {
      effect(() => {
        if(this.dataRepositoryService.tempSelectedUserColumnPreferences()) {
          const taskBankSelectedPreference = this.dataRepositoryService.tempSelectedUserColumnPreferences()?.[SelectedPreferencesENUM.TASKBANK];
          const newPreferenceId = taskBankSelectedPreference?.id ?? '';
          
          const preferenceChanged = this.currentPreferenceId !== newPreferenceId;
          this.currentPreferenceId = newPreferenceId;
          
          this.visibleColumns=this.taskBankColumnService.getVisibleColumns(ColumnName.Unloaded, taskBankSelectedPreference?.columnList??[])
          
          if (preferenceChanged) {
            this.resetTable();
          }
        }else {
        //set the default column list
        this.visibleColumns = this.taskBankColumnService.getVisibleColumns(ColumnName.Unloaded);
        if (this.currentPreferenceId !== '') {
          this.currentPreferenceId = '';
          this.resetTable();
        }
        }
      }, { injector: this.injector })
    }

  /**
   * Resets the table to apply new column configurations
   */
  resetTable() {
    this.showTable = false;
    setTimeout(() => {
      this.showTable = true;
      setTimeout(() => this.applyTaskBankSelectionFilter(), 0);
    }, 0);
  }

  /**
   * This subscription triggers from Map component when certains tasks are selected under drawn polygon
   * & it manages the task selection for task movement
   */
  polygonTaskSelectSubs$(){
    this.taskBankSubscription.push(
      this.dataAccessService.polygonTaskSelection$.subscribe((selectedRows) => {
        if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.Unloaded.toLowerCase()){
          if (selectedRows) {
            const unloadedCollection = this.getUnloadedCollection();

            this.taskMovementService.taskSelectionFromMapPolygon(selectedRows);
            if (this.taskMovementService.isSelectedAll) {
              this.sortingService.resetingCustomSorting();
            }
            if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.unloadedTbCollection.size) {
              this.sortingService.isCustomSort = true;
            }
            else {
              this.setUnloadedSourceData(unloadedCollection);
            }
            this.updateAllowedTaskMoveData(unloadedCollection)
          }
        }
      })
    );
  }

  /**
   * This subscription triggers from Map component when certains tasks are deselected on delete polygon
   * & it manages the task deselection for task movement list
   */
  polygonTaskDeselectSubs$(){
    this.taskBankSubscription.push(
      this.dataAccessService.polygonTaskDeselection$.subscribe((deSelectRows) => {
        if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.Unloaded.toLowerCase()){
          if (deSelectRows) {
            const unloadedCollection = this.getUnloadedCollection();

            deSelectRows.forEach((deselectTask:any)=>{
              if(deselectTask.isMovementAllowed){
                this.taskMovementService.handleMovementForTaskDeselection(deselectTask);
                this.taskMovementService.onTaskUnselection(deselectTask.item_id);
              }
            })
            if (this.taskMovementService.isSelectedAll) {
              this.sortingService.resetingCustomSorting();
            }
            if (this.sortingService.isCustomSortApplied && this.dataRepositoryService.unloadedTbCollection.size) {
              this.sortingService.isCustomSort = true;
            }
            else {
              this.setUnloadedSourceData(unloadedCollection);
            }
            this.updateAllowedTaskMoveData(unloadedCollection)
          }
        }
      })
    );
  }

  /**
   * Helper method to extract unloaded collection without repeated array spreading.
   * Avoids multiple allocations on 5,000+ tasks.
   */
  private getUnloadedCollection(): IUnloadedTasksMap[] {
    return Array.from(this.dataRepositoryService.unloadedTbCollection.values());
  }

  private updateAllowedTaskMoveData(data: IUnloadedTasksMap[]): void {
    this.taskMovementService.allowedTaskMoveData = data.filter((item: IUnloadedTasksMap) => item.isMovementAllowed);
  }

  /**
   * Method runs the effect on task selection. Automatically cleaned up on destroy.
   */
  sortedTaskSelectionEffect(){
    effect(() => {
      const sortedTasks = this.sortingService.$sortedTasksForMovement();
      const dataToUse = sortedTasks.length ? sortedTasks : this.getUnloadedCollection();
      this.setUnloadedSourceData(dataToUse);
    }, { injector: this.injector });
  }

  private setUnloadedSourceData(data: IUnloadedTasksMap[]): void {
    this.unloadedData = this.decorateTaskFilterKeys(data);
    this.applyTaskBankSelectionFilter();
  }

  /**
   * Applies selected Task Bank filter keys via PrimeNG custom filter mode.
   * Debounced to prevent multiple DOM re-renders on rapid updates.
   */
  private applyTaskBankSelectionFilter(): void {
    const selectedKeys = [
      this.selectedTaskFilterServiceCode,
      this.selectedTaskFilterEvent,
    ].filter((key): key is string => !!key);

    this.table?.filter(selectedKeys, this.taskFilterField, 'task-filter-any');
  }

  /**
   * Decorates rows with synthetic filter keys array in O(n) single pass.
   * Avoids redundant filtering per row on large datasets (5,000+ tasks).
   */
  private decorateTaskFilterKeys(data: IUnloadedTasksMap[]): IUnloadedTasksMap[] {

    // TODO: Remove old logic below after new logic works perfectly
    // return data.map((row) => ({
    //   ...row,
    //   [this.taskFilterField]: this.taskFilterKeys.filter(
    //     (key) => !!row[key as keyof IUnloadedTasksMap]
    //   ),
    // }));

    const taskFilterField = this.taskFilterField;
    const taskFilterKeys = this.taskFilterKeys;
     for (const row of data) {
       const selectedKeys: string[] = [];
       for (const key of taskFilterKeys) {
         if (row[key as keyof IUnloadedTasksMap]) {
           selectedKeys.push(key);
         }
       }
       (row as IUnloadedTasksMap & Record<string, string[]>)[taskFilterField] = selectedKeys;
     }
     return data;
  }

  saveColumnWidth(event: any) {
    this.taskBankColumnService.saveColumnWidth(event, this.visibleColumns);
  }

  filterData($event: any) {
    this.table.filterGlobal($event?.target?.value??'', 'contains')
  }
  
  /**
   * Method calls the service method for sorting the dataset
   */
  setSelectedTaskSorting(){
    if(!this.sortingService.isCustomSortApplied){
      this.sortingService.setSelectedTaskSorting()
    }
    else{
      this.sortingService.resetingCustomSorting();
    }
  }

  /**
   * Method is called after PrimeNG's built-in sorting is complete
   * @param event sorting parameters 
   */
  onColumnSort(event: any) {
    if(this.dataRepositoryService.unloadedTbCollection.size){
      this.sortingService.setFlagsOnSortingEvent(event);
    }
    if (this.taskMovementService.isSelectedAll) {
      this.taskMovementService.selectedOptionsForTaskMove = [...this.unloadedData];
    }
  }

  /**
   * Method triggered on select All event, sets the flag and proceeses the task movement 
   * for all items
   * @param event select all checkbox click event
   */
  onSelectAllToggle(event:any){
    if (event.checked) {
      this.sortingService.resetingCustomSorting();
    }
    this.taskMovementService.onSelectAllToggle(event);
  }

  /**
   * Method runs on table row selection/deselection and updates flags and movement 
   * activities
   * @param unloadedData item object from the table row which was selected
   */
  onUnloadedItemSelection(unloadedData: any){
    if(unloadedData.isMovementAllowed) {
      this.taskMovementService.onUnloadedItemSelection(unloadedData);
    }
    if (this.taskMovementService.isSelectedAll) {
      this.taskMovementService.selectedOptionsForTaskMove = [...this.dataRepositoryService.unloadedTbCollection.values()];
      this.sortingService.resetingCustomSorting();
    }
  }

  onUnloadedItemUnselection(unloadedData: any) {
    this.taskMovementService.onTaskUnselection(unloadedData.item_id);
    if (this.taskMovementService.isSelectedAll) {
      this.sortingService.resetingCustomSorting();
      // Deselecting a row means all tasks are no longer selected, so clear the
      // select-all flag; otherwise the follow-up row-select handler re-selects
      // every task via the isSelectedAll branch in onTaskSelection.
      this.taskMovementService.isSelectedAll = false;
    }
  }

  ngOnDestroy(): void {
    if(this.taskBankSubscription?.length) {
      this.taskBankSubscription.forEach(item=>item.unsubscribe());
    }
  }
  barcodeClicked(event: any): void {
    window.open(`#/lookup?barcode=` + event.pn_item_id, "_blank");
  }
  
 
}